import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { X } from "lucide-react";
import { resetStoreReferralStatus, setRequestLegalReviewAsideDetails } from "src/store/slices/onboardingSlice";
import { useNavigate } from "react-router-dom";
import { trackAnalytics } from "src/pages/helpers";
import { EVENT_NAMES, EVENT_PARAMETERS, MODAL_NAMES, getPageName } from "src/utils/EventConstants";

import { resetPaymentStatus } from "src/store/slices/chatSlice";
import { showBlockedThreadWarnPopup } from "src/store/slices/messageSlice";
import Consultation from "./Consultation";
import DrawerSkeleton from "./DrawerSkeleton";

const LegalReviewShelf = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);
  const requestLegalReviewAsideDetails = useSelector((state) => state.onboarding.requestLegalReviewAsideDetails);
  const threadData = useSelector((state) => state.messageSlice.threadData);
  const userMetadata = useSelector((state) => state.onboarding.userMetadata);
  const lastMessageType = threadData?.lastMessage?.message_type;

  const isLoadingHandoffContext = requestLegalReviewAsideDetails?.isLoadingHandoffContext ?? false;

  useEffect(() => {
    if (requestLegalReviewAsideDetails?.show) {
      requestAnimationFrame(() => setIsVisible(true));
    }
  }, [requestLegalReviewAsideDetails?.show]);

  useEffect(() => {
    if (lastMessageType === "deprecated_thread_message") {
      dispatch(showBlockedThreadWarnPopup(true));
      hideReferralDrawer();
      return;
    }
  }, [dispatch]); // eslint-disable-line

  const hideReferralDrawer = () => {
    trackAnalytics(
      EVENT_NAMES.CLOSED_MODAL,
      {
        [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
        [EVENT_PARAMETERS.USER_ID]: userMetadata?.id,
        [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
        [EVENT_PARAMETERS.MODAL_NAME]: MODAL_NAMES.LEGAL_REVIEW,
      },
      userMetadata,
    );
    setIsVisible(false);
    setTimeout(() => {
      dispatch(resetStoreReferralStatus());
      dispatch(setRequestLegalReviewAsideDetails({ show: false }));
      if (window.location.search && !window.location.pathname.includes("/try")) {
        navigate(window.location.pathname);
      }
      dispatch(resetPaymentStatus());
    }, 300);
  };

  return (
    <>
      {requestLegalReviewAsideDetails?.show && (
        <div
          className={`fixed inset-0 z-[1100] transition-opacity duration-300 touch-none ${isVisible ? "bg-[rgba(0,0,0,0.5)]" : "bg-transparent"}`}
          onClick={hideReferralDrawer}
        >
          <aside
            data-testid="consultation-drawer"
            className={`flex flex-col overflow-hidden overscroll-contain bg-white shadow-xl absolute z-[1200] right-0 max-h-[88dvh] bottom-0 w-full rounded-t-[20px] md:h-screen md:max-h-none md:top-0 md:bottom-auto md:w-[591px] md:rounded-none md:border-l md:border-l-[#E4E4E4] transition-transform duration-300 ease-out ${isVisible ? "translate-x-0 md:translate-x-0 translate-y-0" : "translate-y-full md:translate-y-0 md:translate-x-full"}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col h-full overflow-hidden">



              {/* Mobile close button */}
              <div className="flex md:hidden absolute right-3 top-3 z-10">
                <button
                  data-testid="consultation-drawer-close-mobile"
                  className="flex items-center justify-center w-[40px] h-[40px] rounded-full cursor-pointer text-[#141414] hover:bg-[#E4E4E4] transition-colors"
                  onClick={hideReferralDrawer}
                >
                  <span className="text-[26px] leading-none">&times;</span>
                </button>
              </div>

              {/* Desktop close button */}
              <div className="hidden md:flex justify-end w-full px-6 py-3 absolute right-0 top-0 z-10">
                <button
                  data-testid="consultation-drawer-close-desktop"
                  className="flex items-center my-auto text-sm cursor-pointer font-semibold text-[#353535]"
                  onClick={hideReferralDrawer}
                >
                  <X size={16} />
                </button>
              </div>

              {/* Main Content — two states: loading skeleton, available */}
              <div className="flex-1 min-h-0 h-0">
                {isLoadingHandoffContext ? (
                  <DrawerSkeleton />
                ) : (
                  <Consultation />
                )}
              </div>
            </div>
          </aside>
        </div>
      )}
    </>
  );
};

export default LegalReviewShelf;
